import { Accent, Section, displayTitle } from "@/features/portal/components/portal-ui";
import { ProgressRing } from "@/features/portal/components/progress-ring";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

/** Six weeks of planned work: how tall the week is, and how much of it got done. */
const WEEKS = [
  { n: 1, planned: 55, done: 40 },
  { n: 2, planned: 70, done: 62 },
  { n: 3, planned: 45, done: 45 },
  { n: 4, planned: 80, done: 70 },
  { n: 5, planned: 92, done: 84 },
  { n: 6, planned: 74, done: 30 },
];

/** A lift creeping up with the odd bad day, as a progress chart honestly looks. */
const LIFT =
  "M0,150 L30,140 L50,146 L70,120 L90,126 L110,100 L125,108 L140,70 L150,96 L165,90 L180,40 L192,92 L210,84 L228,60 L240,88 L258,74 L275,44 L290,80 L300,70";
/** The peak of that line, in the same 300 x 170 box: the day the record fell. */
const RECORD = { x: 180, y: 40 };

const card = "flex min-h-[460px] flex-col rounded-[28px] border border-border bg-card p-7";
const cardTitle = "text-3xl leading-[1.05] font-medium tracking-[-0.035em]";
const cardBody = "mt-3.5 text-[15px] leading-normal";

/** The same three things the athlete sees, from the coach's side: plan, progress and diet. */
export const CoachView = () => (
  <Section>
    <h2 className={cn(displayTitle, "mb-12 px-1 text-center")}>
      {m.portal_view_title_lead()} <Accent>{m.portal_view_title_accent()}</Accent> {m.portal_view_title_tail()}
    </h2>
    <div className="grid gap-3 lg:grid-cols-3">
      <article className={cn(card, "border-transparent bg-primary text-primary-foreground")}>
        <h3 className={cardTitle}>{m.portal_view_plan_title()}</h3>
        <p className={cn(cardBody, "opacity-75")}>{m.portal_view_plan_body()}</p>
        <div aria-hidden className="mt-auto pt-7">
          <div className="flex h-[170px] items-end gap-2.5">
            {WEEKS.map((week) => (
              <span
                key={week.n}
                className="relative flex-1 overflow-hidden rounded-xl bg-primary-foreground/20"
                style={{ height: `${week.planned}%` }}
              >
                {/* The same radius as the track, so a full week fills it edge to edge. */}
                <i
                  className="absolute inset-x-0 bottom-0 rounded-xl bg-primary-foreground"
                  style={{ height: `${(week.done / week.planned) * 100}%` }}
                />
              </span>
            ))}
          </div>
          <div className="mt-2.5 flex gap-2.5 text-xs opacity-65">
            {WEEKS.map((week) => (
              <span key={week.n} className="flex-1 text-center">
                {m.portal_view_week({ n: week.n })}
              </span>
            ))}
          </div>
        </div>
      </article>

      <article className={card}>
        <h3 className={cardTitle}>{m.portal_view_progress_title()}</h3>
        <p className={cn(cardBody, "text-muted-foreground")}>{m.portal_view_progress_body()}</p>
        <div aria-hidden className="mt-auto pt-16">
          <div className="relative h-[170px]">
            <svg viewBox="0 0 300 170" preserveAspectRatio="none" className="absolute inset-0 size-full">
              <defs>
                <linearGradient id="portal-lift-fill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="currentColor" stopOpacity="0.18" />
                  <stop offset="1" stopColor="currentColor" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={`${LIFT} L300,170 L0,170Z`} fill="url(#portal-lift-fill)" />
              <path d={LIFT} fill="none" stroke="currentColor" strokeWidth="2" vectorEffect="non-scaling-stroke" />
            </svg>
            {/* The record is drawn over the chart rather than in it: the chart stretches to the
                card, and a dot inside it would stretch into an oval. */}
            <div
              className="absolute bottom-0 -translate-x-1/2"
              style={{ left: `${(RECORD.x / 300) * 100}%`, top: `${(RECORD.y / 170) * 100}%` }}
            >
              <span className="absolute inset-y-0 left-1/2 -translate-x-1/2 border-l-2 border-dashed border-primary/60" />
              <span className="absolute top-0 left-1/2 size-3.5 -translate-1/2 rounded-full bg-primary shadow-[0_0_0_5px_color-mix(in_oklch,var(--primary)_25%,transparent),0_0_18px_var(--primary)]" />
              <span className="absolute bottom-full left-1/2 mb-4 -translate-x-1/2 rounded-xl bg-primary px-3 py-1.5 text-center whitespace-nowrap text-primary-foreground">
                <b className="block text-xs font-semibold">{m.portal_view_record()}</b>
                <small className="block text-[11px] opacity-75">{m.portal_view_record_detail()}</small>
              </span>
            </div>
          </div>
        </div>
      </article>

      <article className={card}>
        <h3 className={cardTitle}>{m.portal_view_diet_title()}</h3>
        <p className={cn(cardBody, "text-muted-foreground")}>{m.portal_view_diet_body()}</p>
        <div className="mt-auto pt-7">
          <div className="relative grid place-items-center">
            <ProgressRing share={1840 / 2400} size={190} stroke={26} />
            <div className="absolute text-center">
              <b className="block text-3xl font-medium tracking-[-0.04em] tabular-nums">
                {(1840).toLocaleString(getLocale())}
              </b>
              <small className="text-xs text-muted-foreground">{m.portal_view_diet_of()}</small>
            </div>
          </div>
          <div className="mt-[18px] flex justify-center gap-5 text-[13px]">
            <span className="inline-flex items-center gap-2">
              <i className="size-3.5 rounded-full bg-primary" />
              {m.portal_view_diet_eaten()}
            </span>
            <span className="inline-flex items-center gap-2">
              <i className="size-3.5 rounded-full bg-[var(--site-well)]" />
              {m.portal_view_diet_left()}
            </span>
          </div>
        </div>
      </article>
    </div>
  </Section>
);
